import { useEffect, useRef, useState } from "react";
import { cleanFileName, sniffImageType, type ManifestFile } from "@timecapsule/crypto";
import { fetchFile, formatBytes } from "../attachments";
import { errorText, useT } from "../i18n";

/**
 * Files of an opened capsule (PLAN.md 4.11 H10). A blob: URL made here has the vrelis.com origin,
 * so a sender's HTML or SVG opened in a tab or frame would run as this app. Therefore:
 * - inline only JPEG, PNG, GIF and WebP, as <img>, after their magic bytes confirm the type
 *   (the type in the manifest is the sender's claim and is never used for display);
 * - everything else, SVG, HTML and PDF included, only as a download: application/octet-stream
 *   with <a download>; the app never navigates to or frames a blob: URL;
 * - the file name is cleaned before it is shown or used.
 */
export function Attachments({ files }: { files: ManifestFile[] }) {
  return (
    <ul className="attachments">
      {files.map((f) => (
        <Attachment key={f.sha256} file={f} />
      ))}
    </ul>
  );
}

function Attachment({ file }: { file: ManifestFile }) {
  const t = useT();
  const name = cleanFileName(file.name);
  const bytes = useRef<Uint8Array | null>(null);
  const [image, setImage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Files that claim to be images are fetched at once; they are shown only if the bytes agree.
  useEffect(() => {
    if (!file.type.startsWith("image/")) return;
    let url: string | null = null;
    let cancelled = false;
    fetchFile(file).then(
      (b) => {
        bytes.current = b;
        const type = sniffImageType(b);
        if (type && !cancelled) {
          url = URL.createObjectURL(new Blob([b.slice().buffer as ArrayBuffer], { type }));
          setImage(url);
        }
      },
      (err: unknown) => !cancelled && setError(errorText(err)),
    );
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [file]);

  async function download() {
    setError(null);
    setBusy(true);
    try {
      bytes.current ??= await fetchFile(file);
      const url = URL.createObjectURL(new Blob([bytes.current.slice().buffer as ArrayBuffer], { type: "application/octet-stream" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      a.rel = "noopener";
      document.body.append(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      setError(errorText(err));
    }
    setBusy(false);
  }

  return (
    <li>
      {image && <img src={image} alt={name} />}
      <div>
        <span className="file-name">{name}</span> <span className="muted small">{formatBytes(file.size)}</span>{" "}
        <button className="link small" onClick={() => void download()} disabled={busy}>
          {busy ? t.common.downloading : t.common.download}
        </button>
      </div>
      {error && <p className="error small">{error}</p>}
    </li>
  );
}
